/**
 * TODO
 * WIP
 */
import React, { PropsWithChildren } from 'react';

import { css, cx } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { DeviceType } from '@/models/MedicalDeviceModel.ts';
import { toDateTime } from '@/utils/dateUtils.ts';

export const SvgDataTooltip: React.FC<PropsWithChildren> = ({ children }) => {
  const styles = useStyles(makeStyles);

  return <div className={styles.tooltip}>{children}</div>;
};

export const SvgDataTooltipLabel: React.FC<{
  label: string;
  emphasis?: boolean;
}> = ({ label, emphasis }) => {
  const styles = useStyles(makeStyles);
  return (
    <Typography
      variant="bodySmall"
      component="span"
      className={cx({ [styles.emphasis]: emphasis })}
    >
      {label}
    </Typography>
  );
};

export const SvgDataTooltipValue: React.FC<{
  value: string | number;
  emphasis?: boolean;
}> = ({ value, emphasis }) => {
  const styles = useStyles(makeStyles);

  return (
    <Typography
      variant="bodySmall"
      component="span"
      className={cx({ [styles.emphasis]: emphasis })}
    >
      {value}
    </Typography>
  );
};

export const SvgDataTooltipTitle: React.FC<{
  title: string;
}> = ({ title }) => {
  const styles = useStyles(makeStyles);
  return (
    <Typography variant="bodyLarge" className={styles.title}>
      {title}
    </Typography>
  );
};

export const SvgDataTooltipSubtitle: React.FC<{
  subtitle: string;
}> = ({ subtitle }) => {
  const styles = useStyles(makeStyles);
  return (
    <Typography variant="body" className={styles.content}>
      {subtitle}
    </Typography>
  );
};

export const SvgDataTooltipKeyValueContent: React.FC<{
  label: string;
  value: string;
  emphasis?: 'title' | 'value' | 'all' | 'none';
}> = ({ label, value, emphasis = 'title' }) => {
  const styles = useStyles(makeStyles);
  return (
    <div className={styles.item}>
      <SvgDataTooltipLabel
        label={label}
        emphasis={emphasis === 'all' || emphasis === 'title'}
      />
      <SvgDataTooltipValue
        value={value}
        emphasis={emphasis === 'all' || emphasis === 'value'}
      />
    </div>
  );
};

export const SvgDataTooltipFooter: React.FC<{
  date: string;
  inputMethod?: DeviceType;
}> = ({ date, inputMethod }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const caption =
    date && inputMethod
      ? `${toDateTime(date).toLocaleString(DateTime.DATETIME_SHORT)} • ${t(`charts.inputMethod.${inputMethod}`)}`
      : date
        ? toDateTime(date).toLocaleString(DateTime.DATETIME_SHORT)
        : inputMethod
          ? t(`charts.inputMethod.${inputMethod}`)
          : undefined;

  return caption ? (
    <Typography variant="caption" className={styles.caption}>
      {caption}
    </Typography>
  ) : null;
};
const makeStyles = (theme: Theme) => ({
  tooltip: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 8)};
  `,
  item: css`
    display: flex;
    flex-direction: row;
    max-width: ${theme.spacing(192)};
    width: 100%;
    justify-content: space-between;
    align-items: baseline;
    gap: ${theme.spacing(8)};
  `,
  emphasis: css`
    font-weight: bolder;
  `,
  caption: css`
    max-width: ${theme.spacing(192)};
    text-align: center;
  `,
  content: css`
    word-break: break-all;
    white-space: pre-wrap;
  `,
  title: css`
    font-weight: bold;
  `,
});
